// src/pages/auth-callback/index.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { authService } from "../../services/authService";
import Icon from "../../components/AppIcon";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [message, setMessage] = useState("Verificando tu cuenta...");

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check if this is an email confirmation callback
        const hashParams = new URLSearchParams(window?.location?.hash?.substring(1));
        const accessToken = hashParams?.get('access_token');
        const type = hashParams?.get('type');
        
        // If we have an access token and it's a signup confirmation
        if (accessToken && type === 'signup') {
          setMessage("Confirmando tu correo electrónico...");
          
          // Exchange the token for a session
          const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
          
          if (sessionError) {
            console.error('Session error:', sessionError);
            setStatus("error");
            setMessage("Error al confirmar tu cuenta. Por favor, intenta iniciar sesión.");
            setTimeout(() => navigate('/signin', { replace: true }), 3000);
            return;
          }
          
          if (sessionData?.session?.user) {
            const user = sessionData?.session?.user;
            
            // Wait for profile to be created/loaded
            setMessage("Cargando tu perfil...");
            const profile = await authService?.waitForProfile(user?.id, 100); // Wait up to 10 seconds
            
            if (profile) {
              setStatus("success");
              setMessage("¡Cuenta verificada exitosamente! Redirigiendo...");
              
              // Role-based redirect
              setTimeout(() => {
                if (profile?.role === 'admin') {
                  navigate('/admin-dashboard', { replace: true });
                } else {
                  navigate('/user-dashboard', { replace: true });
                }
              }, 1500);
            } else {
              // Profile not found but user exists - still redirect
              setStatus("success");
              setMessage("Cuenta verificada. Redirigiendo...");
              setTimeout(() => navigate('/user-dashboard', { replace: true }), 1500);
            }
          } else {
            setStatus("error");
            setMessage("No se pudo obtener la sesión. Por favor, inicia sesión.");
            setTimeout(() => navigate('/signin', { replace: true }), 3000);
          }
        } else {
          // No token in URL - check if user is already logged in
          const { user, error: userError } = await authService?.getCurrentUser();
          
          if (userError || !user) {
            setStatus("error");
            setMessage("Error al verificar tu cuenta. Por favor, intenta iniciar sesión.");
            setTimeout(() => navigate('/signin', { replace: true }), 3000);
            return;
          }

          // User is logged in, load profile
          setMessage("Cargando tu perfil...");
          const profile = await authService?.waitForProfile(user?.id);
          
          if (profile) {
            setStatus("success");
            setMessage("¡Cuenta verificada exitosamente! Redirigiendo...");
            
            setTimeout(() => {
              if (profile?.role === 'admin') {
                navigate('/admin-dashboard', { replace: true });
              } else {
                navigate('/user-dashboard', { replace: true });
              }
            }, 1500);
          } else {
            setStatus("success");
            setMessage("Cuenta verificada. Redirigiendo...");
            setTimeout(() => navigate('/user-dashboard', { replace: true }), 1500);
          }
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus("error");
        setMessage("Error al procesar la verificación. Redirigiendo a inicio de sesión...");
        setTimeout(() => navigate('/signin', { replace: true }), 3000);
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="card p-8">
          {status === 'processing' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4 animate-pulse">
                <Icon name="Mail" size={32} color="var(--color-primary)" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Verificando Cuenta
              </h2>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
                <Icon name="CheckCircle" size={32} color="var(--color-success)" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                ¡Verificación Exitosa!
              </h2>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-error/10 rounded-full mb-4">
                <Icon name="AlertCircle" size={32} color="var(--color-error)" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Error de Verificación
              </h2>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
